import React from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LoginIcon from '@mui/icons-material/Login';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import MainContent from '../components/MainContent';
import RightBar from '../components/RightBar';
import Calendar from '../components/calendar/Calendar';
import ReportCard from '../components/analytics/ReportCard';
import usePermissions from '../hooks/usePermissions';
import { isAdminOrAbove } from '../utils/roles';

// permission: null = visible to everyone who can open this page.
const REPORT_SECTIONS = [
  {
    title: 'Engagement',
    reports: [
      {
        name: 'Cohort Logins',
        description:
          'Login activity and engagement for students, broken down by cohort.',
        icon: <LoginIcon fontSize="inherit" />,
        to: '/analytics/cohort-logins-analytics',
        permission: 'ViewScholarshipLoginsAnalytics',
      },
      {
        name: 'Cohort Group Members',
        description: 'Group membership lists for each cohort.',
        icon: <GroupOutlinedIcon fontSize="inherit" />,
        to: '/analytics/cohort-group-members',
        permission: null,
      },
      {
        name: 'Self-paced Learners',
        description:
          'Learners outside a regular cohort: how many, how many are active, and who they are.',
        icon: <SelfImprovementIcon fontSize="inherit" />,
        to: '/analytics/self-paced',
        permission: null,
      },
    ],
  },
  {
    title: 'Assessments',
    reports: [
      {
        name: 'Cohort Quiz Analytics',
        description: 'Quiz submissions and performance by gender and cohort.',
        icon: <QuizOutlinedIcon fontSize="inherit" />,
        to: '/analytics/cohort-quiz-analytics',
        permission: 'ViewScholarshipQuizAnalytics',
      },
      {
        name: 'Cohort Weekly Assignment Analytics',
        description: 'Weekly assignment submissions and performance by cohort.',
        icon: <AssignmentOutlinedIcon fontSize="inherit" />,
        to: '/analytics/cohort-assignment-analytics',
        permission: 'ViewScholarshipAssignmentAnalytics',
      },
      {
        name: 'Cohort Survey Analytics',
        description: 'Weekly survey responses, segmented by cohort.',
        icon: <PollOutlinedIcon fontSize="inherit" />,
        to: '/analytics/survey-analytics',
        permission: null,
      },
    ],
  },
  {
    title: 'Progress and certification',
    reports: [
      {
        name: 'Sponsored Cohort Module Progress',
        description:
          'Learner progression across modules and courses by sponsored cohort.',
        icon: <TrendingUpIcon fontSize="inherit" />,
        to: '/analytics/cohort-module-progress-analytics',
        permission: 'ViewStudentsProgressAnalytics',
      },
      {
        name: 'Cohort Certification Analytics',
        description:
          'Submitted final projects and learners approved for certification.',
        icon: <WorkspacePremiumOutlinedIcon fontSize="inherit" />,
        to: '/analytics/cohort-certification-analytics',
        permission: 'ViewScholarshipCertificationAnalytics',
      },
    ],
  },
  {
    title: 'Paid modules',
    reports: [
      {
        name: 'Paid Modules Student Progress',
        description:
          'Learner progression across paid modules and courses per paid cohort.',
        icon: <TimelineIcon fontSize="inherit" />,
        to: '/analytics/paid-modules-students-progress',
        permission: 'ViewStudentsProgressAnalytics',
      },
      {
        name: 'Paid Modules Analytics',
        description: 'Paid module enrolment and revenue generated.',
        icon: <MonetizationOnOutlinedIcon fontSize="inherit" />,
        to: '/analytics/paid-modules',
        permission: 'ViewPaidModulesAnalytics',
      },
    ],
  },
  {
    title: 'Administration',
    adminOnly: true,
    reports: [
      {
        name: 'Administrator Daily Logins',
        description: 'Daily login counts for staff accounts.',
        icon: <AdminPanelSettingsOutlinedIcon fontSize="inherit" />,
        to: '/analytics/admin-logins',
        permission: null,
      },
    ],
  },
];

function AnalyticsDashBoard() {
  const permissions = usePermissions();
  const userRole = useSelector((state) => state.auth.userDetails?.role);

  const sections = REPORT_SECTIONS.filter(
    (section) => !section.adminOnly || isAdminOrAbove(userRole)
  )
    .map((section) => ({
      ...section,
      reports: section.reports.filter(
        (report) =>
          !report.permission || permissions.includes(report.permission)
      ),
    }))
    .filter((section) => section.reports.length > 0);

  return (
    <>
      <MainContent>
        <div className="space-y-6 pb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
            <Breadcrumbs
              aria-label="breadcrumb"
              separator={<NavigateNextIcon fontSize="small" />}
              className="text-xs"
            >
              <NavLink to="/" className="flex items-center text-gray-500">
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                <span className="text-xs">Home</span>
              </NavLink>
              <span className="text-xs text-gray-900">Analytics</span>
            </Breadcrumbs>
            <h1 className="text-lg font-semibold text-gray-900 mt-1">
              Analytics
            </h1>
            <p className="text-xs text-gray-500">
              Reports on learner engagement, assessments, progress and paid
              modules
            </p>
          </div>

          {sections.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-600">
              You don&apos;t have access to any analytics reports yet. Ask an
              administrator for the permissions you need.
            </div>
          ) : (
            sections.map((section) => (
              <section key={section.title}>
                <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">
                  {section.title}
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {section.reports.map((report) => (
                    <ReportCard
                      key={report.to}
                      name={report.name}
                      description={report.description}
                      icon={report.icon}
                      to={report.to}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </MainContent>
      <RightBar>
        <Calendar />
      </RightBar>
    </>
  );
}

export default AnalyticsDashBoard;
